import {
    Children,
    cloneElement,
    type ComponentPropsWithoutRef,
    type FocusEventHandler,
    isValidElement,
    type MouseEventHandler,
    type ReactElement,
    type ReactNode,
    type Ref,
    useCallback,
    useEffect,
    useId,
    useMemo,
    useRef,
    useState,
} from 'react';

import { createPortal } from 'react-dom';

import { alignConfig, getArrowMargin, positionConfig, sizeConfig, variantConfig } from './config';
import {
    type CursorPosition,
    TooltipContext,
    type TooltipContextValue,
    TooltipProviderContext,
    type TooltipProviderContextValue,
    useTooltip,
    useTooltipProvider,
} from './context';
import {
    TooltipAlign,
    type TooltipAlignType,
    type TooltipDelayConfig,
    TooltipPosition,
    type TooltipPositionType,
    TooltipSize,
    type TooltipSizeType,
    TooltipVariant,
    type TooltipVariantType,
} from './types';
import { useTooltipPosition } from './useTooltipPosition';
import { cn } from '../../utils/cn';

export interface TooltipProviderProps extends TooltipProviderContextValue {
    children: ReactNode;
}

/**
 * Provides global configuration for all tooltips within its tree.
 *
 * @param delayShow - Default delay in ms before showing tooltips. Defaults to 0.
 * @param delayHide - Default delay in ms before hiding tooltips. Defaults to 0.
 * @param skipDelayOnOpen - Skip show delay when another tooltip was recently open. Defaults to false.
 * @param children - Child components that will inherit these defaults.
 *
 * @example
 * <TooltipProvider delayShow={200} delayHide={100}>
 *   <App />
 * </TooltipProvider>
 */
export const TooltipProvider: React.FC<TooltipProviderProps> = ({
    children,
    delayHide = 0,
    delayShow = 0,
    skipDelayOnOpen = false,
}) => (
    <TooltipProviderContext.Provider value={{ delayHide, delayShow, skipDelayOnOpen }}>
        {children}
    </TooltipProviderContext.Provider>
);

export interface TooltipProps extends TooltipDelayConfig {
    children: ReactNode;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
    open?: boolean;
}

/**
 * Root component for tooltip functionality. Manages open/close state and provides context to child components.
 *
 * @param children - TooltipTrigger and TooltipContent components.
 * @param defaultOpen - Initial open state for uncontrolled usage. Defaults to false.
 * @param open - Controlled open state.
 * @param onOpenChange - Callback fired when open state changes.
 * @param delayShow - Delay in ms before showing the tooltip. Overrides TooltipProvider value.
 * @param delayHide - Delay in ms before hiding the tooltip. Overrides TooltipProvider value.
 *
 * @example
 * <Tooltip>
 *   <TooltipTrigger asChild>
 *     <button>Hover me</button>
 *   </TooltipTrigger>
 *   <TooltipContent>Helpful tooltip text</TooltipContent>
 * </Tooltip>
 */
export const Tooltip: React.FC<TooltipProps> = ({
    children,
    defaultOpen = false,
    delayHide: delayHideProp,
    delayShow: delayShowProp,
    onOpenChange,
    open: openProp,
}) => {
    const providerConfig = useTooltipProvider();
    const [internalOpen, setInternalOpen] = useState(defaultOpen);
    const [position, setPosition] = useState<TooltipPositionType>(TooltipPosition.TOP);
    const [cursorPosition, setCursorPosition] = useState<CursorPosition>({ x: 0, y: 0 });
    const triggerRef = useRef<HTMLElement | null>(null);
    const contentId = useId();

    const showTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const isControlled = openProp !== undefined;
    const isOpen = isControlled ? openProp : internalOpen;

    const delayShow = delayShowProp ?? providerConfig.delayShow ?? 0;
    const delayHide = delayHideProp ?? providerConfig.delayHide ?? 0;

    const clearTimeouts = useCallback(() => {
        if (showTimeoutRef.current) {
            clearTimeout(showTimeoutRef.current);
            showTimeoutRef.current = null;
        }
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
            hideTimeoutRef.current = null;
        }
    }, []);

    const scheduleStateChange = useCallback(
        (open: boolean, delay: number) => {
            clearTimeouts();
            const apply = () => {
                if (!isControlled) {
                    setInternalOpen(open);
                }
                onOpenChange?.(open);
            };
            if (delay > 0) {
                const ref = open ? showTimeoutRef : hideTimeoutRef;
                ref.current = setTimeout(apply, delay);
            } else {
                apply();
            }
        },
        [clearTimeouts, isControlled, onOpenChange],
    );

    const onOpen = useCallback(() => scheduleStateChange(true, delayShow), [scheduleStateChange, delayShow]);
    const onClose = useCallback(() => scheduleStateChange(false, delayHide), [scheduleStateChange, delayHide]);

    const contextValue = useMemo<TooltipContextValue>(
        () => ({
            contentId,
            cursorPosition,
            isOpen,
            onClose,
            onOpen,
            position,
            setCursorPosition,
            setPosition,
            triggerRef,
        }),
        [contentId, cursorPosition, isOpen, onClose, onOpen, position],
    );

    return <TooltipContext.Provider value={contextValue}>{children}</TooltipContext.Provider>;
};

export interface TooltipTriggerProps {
    asChild?: boolean;
    children: ReactNode;
}

/**
 * The element that triggers the tooltip on hover or focus.
 *
 * @param asChild - When true, the trigger will be the child element instead of a wrapper span.
 * @param children - The trigger element(s).
 *
 * @example
 * <TooltipTrigger asChild>
 *   <button>Hover me</button>
 * </TooltipTrigger>
 */

interface TriggerChildEventProps {
    onMouseEnter?: MouseEventHandler;
    onMouseLeave?: MouseEventHandler;
    onMouseMove?: MouseEventHandler;
    onFocus?: FocusEventHandler;
    onBlur?: FocusEventHandler;
}

export const TooltipTrigger: React.FC<TooltipTriggerProps> = ({ asChild = false, children }) => {
    const { contentId, isOpen, onClose, onOpen, setCursorPosition, triggerRef } = useTooltip();

    const handlers = useMemo(() => {
        const forward =
            <E extends React.SyntheticEvent>(action: () => void, propName: keyof TriggerChildEventProps) =>
            (event: E) => {
                action();
                const child = Children.only(children);
                if (isValidElement<TriggerChildEventProps>(child)) {
                    (child.props[propName] as ((e: E) => void) | undefined)?.(event);
                }
            };

        const forwardMouseMove = (event: React.MouseEvent) => {
            setCursorPosition({ x: event.clientX, y: event.clientY });
            const child = Children.only(children);
            if (isValidElement<TriggerChildEventProps>(child)) {
                child.props.onMouseMove?.(event);
            }
        };

        return {
            onBlur: forward(onClose, 'onBlur'),
            onFocus: forward(onOpen, 'onFocus'),
            onMouseEnter: forward(onOpen, 'onMouseEnter'),
            onMouseLeave: forward(onClose, 'onMouseLeave'),
            onMouseMove: forwardMouseMove,
        };
    }, [children, onClose, onOpen, setCursorPosition]);

    const triggerProps = {
        'aria-describedby': isOpen ? contentId : undefined,
        ...handlers,
        ref: triggerRef,
    };

    if (asChild && isValidElement(children)) {
        const child = children as ReactElement<Record<string, unknown> & { ref?: Ref<HTMLElement> }>;
        const childRef = child.props.ref;
        const mergedRef = (node: HTMLElement | null) => {
            triggerRef.current = node;
            if (typeof childRef === 'function') {
                childRef(node);
            } else if (childRef) {
                childRef.current = node;
            }
        };

        return cloneElement(child, { ...triggerProps, ref: mergedRef });
    }

    return (
        <span
            aria-describedby={triggerProps['aria-describedby']}
            onBlur={triggerProps.onBlur}
            onFocus={triggerProps.onFocus}
            onMouseEnter={triggerProps.onMouseEnter}
            onMouseLeave={triggerProps.onMouseLeave}
            onMouseMove={triggerProps.onMouseMove}
            ref={triggerRef as React.RefObject<HTMLSpanElement>}
            style={{ display: 'inline-block' }}
        >
            {children}
        </span>
    );
};

export interface TooltipContentProps extends Omit<ComponentPropsWithoutRef<'div'>, 'children'> {
    align?: TooltipAlignType;
    children: ReactNode;
    containerClassName?: string;
    side?: TooltipPositionType;
    sideOffset?: number;
    size?: TooltipSizeType;
    variant?: TooltipVariantType;
    hideArrow?: boolean;
}

interface ArrowProps {
    align: TooltipAlignType;
    arrowOffset?: number;
    position: TooltipPositionType;
    size: TooltipSizeType;
    variant: TooltipVariantType;
}

const Arrow: React.FC<ArrowProps> = ({ align, arrowOffset, position, size, variant }) => {
    const { arrow: arrowColor } = variantConfig[variant];
    const { arrow: arrowSize } = sizeConfig[size];
    const { arrowOrder, arrowRotation } = positionConfig[position];
    const isHorizontal = position === TooltipPosition.TOP || position === TooltipPosition.BOTTOM;
    const baseMargin = getArrowMargin(position, size);

    const hasOffset = arrowOffset !== undefined;
    const alignClass = hasOffset
        ? isHorizontal
            ? 'justify-start'
            : 'items-start'
        : isHorizontal
        ? alignConfig[align].horizontal
        : alignConfig[align].vertical;

    const offsetStyle: React.CSSProperties = hasOffset
        ? isHorizontal
            ? { paddingLeft: arrowOffset }
            : { paddingTop: arrowOffset }
        : {};

    return (
        <div className={cn('flex shrink-0', alignClass, arrowOrder)} style={{ ...baseMargin, ...offsetStyle }}>
            <svg
                aria-hidden="true"
                className={cn(arrowRotation, arrowColor)}
                height={arrowSize.height}
                viewBox={`0 0 ${arrowSize.width} ${arrowSize.height}`}
                width={arrowSize.width}
            >
                <path
                    d={`M${arrowSize.width / 2} 0L${arrowSize.width} ${arrowSize.height}H0L${arrowSize.width / 2} 0Z`}
                />
            </svg>
        </div>
    );
};

const ARROW_EDGE_PADDING = 8;

const calculateArrowOffset = (
    cursorPosition: { x: number; y: number },
    tooltipPosition: { x: number; y: number },
    actualSide: TooltipPositionType,
    arrowSize: { width: number; height: number },
    contentElement: HTMLElement | null,
): number | undefined => {
    if (cursorPosition.x === 0 && cursorPosition.y === 0) {
        return undefined;
    }

    if (!contentElement) {
        return undefined;
    }

    const isHorizontal = actualSide === TooltipPosition.TOP || actualSide === TooltipPosition.BOTTOM;
    const contentRect = contentElement.getBoundingClientRect();

    if (isHorizontal) {
        const tooltipScreenX = tooltipPosition.x - window.scrollX;
        const rawOffset = cursorPosition.x - tooltipScreenX - arrowSize.width / 2;
        const maxOffset = contentRect.width - arrowSize.width - ARROW_EDGE_PADDING;
        return Math.max(ARROW_EDGE_PADDING, Math.min(rawOffset, maxOffset));
    }

    const tooltipScreenY = tooltipPosition.y - window.scrollY;
    const rawOffset = cursorPosition.y - tooltipScreenY - arrowSize.height / 2;
    const maxOffset = contentRect.height - arrowSize.height - ARROW_EDGE_PADDING;
    return Math.max(ARROW_EDGE_PADDING, Math.min(rawOffset, maxOffset));
};

/**
 * The content that appears as the tooltip. Renders in a portal for proper stacking.
 *
 * @param side - Which side of the trigger the tooltip appears. Defaults to "top".
 * @param align - Alignment along the side. Defaults to "center".
 * @param sideOffset - Distance from the trigger in pixels. Defaults to 4.
 * @param variant - Visual style of the tooltip. Defaults to "dark".
 * @param size - Size of the tooltip. Defaults to "small".
 * @param children - Tooltip content.
 * @param className - Additional CSS classes.
 *
 * @example
 * <TooltipContent side="bottom" variant="light">
 *   Helpful information
 * </TooltipContent>
 */
export const TooltipContent: React.FC<TooltipContentProps> = ({
    align = TooltipAlign.CENTER,
    children,
    className,
    containerClassName,
    side = TooltipPosition.TOP,
    sideOffset = 4,
    size = TooltipSize.SMALL,
    variant = TooltipVariant.DARK,
    hideArrow = false,
    ...props
}) => {
    const { contentId, cursorPosition, isOpen, setPosition, triggerRef } = useTooltip();
    const [contentElement, setContentElement] = useState<HTMLElement | null>(null);

    const {
        actualSide,
        contentRef: positionContentRef,
        position,
    } = useTooltipPosition({
        align,
        side,
        sideOffset,
        triggerRef,
    });

    const contentRef = useCallback(
        (node: HTMLElement | null) => {
            positionContentRef(node);
            setContentElement(node);
        },
        [positionContentRef],
    );

    useEffect(() => {
        setPosition(actualSide);
    }, [actualSide, setPosition]);

    if (!isOpen) {
        return null;
    }

    const { content: variantContent } = variantConfig[variant];
    const { content: sizeContent } = sizeConfig[size];
    const { container } = positionConfig[actualSide];
    const { arrow: arrowSize } = sizeConfig[size];

    const arrowOffset = calculateArrowOffset(cursorPosition, position, actualSide, arrowSize, contentElement);

    // Placed with a transform instead of left/top on purpose: an absolutely positioned element
    // without an explicit width shrinks to fit `containing block width - left`. Feeding the
    // measured width back into `left` would shrink the tooltip again on every ResizeObserver pass,
    // visibly narrowing it over many frames after it opened. A transform does not affect layout,
    // so the width is final on the first paint.
    const content = (
        <div
            ref={contentRef}
            className={cn('flex items-stretch absolute z-50 left-0 top-0', container, containerClassName)}
            id={contentId}
            role="tooltip"
            style={{
                transform: `translate(${Math.round(position.x)}px, ${Math.round(position.y)}px)`,
            }}
            {...props}
        >
            {!hideArrow && (
                <Arrow align={align} arrowOffset={arrowOffset} position={actualSide} size={size} variant={variant} />
            )}
            <div
                className={cn(
                    'rounded-md text-sm font-normal leading-[1.46] max-w-sm break-words',
                    variantContent,
                    sizeContent,
                    className,
                )}
            >
                {children}
            </div>
        </div>
    );

    return createPortal(content, document.body);
};
